/* ─────────────────────────────────────────────────────────────
   CONSENSUS-ANCHOR NOTE BOX

   Reproduces the artifact's note box that discloses the consensus anchor:
   years 1–2 net income is pinned to analyst FY1/FY2 consensus EPS × diluted
   shares (year-1 revenue to consensus revenue), then fades back to the model's
   own path. Momentum decides how long the anchor persists — `fade_den: 6` is
   the extended fade (price ≥ 1.08× its 200-DMA and not a cyclical peak, so the
   terminal year keeps half the year-2 anchor ratio) versus `fade_den: 3`,
   where the anchor is gone by the terminal year.

   Applies to the DCF_FCFE_PROJ and DCF_FCFE_BANK engines. When `on` is false
   the model ran its own organic path and that is stated plainly.
───────────────────────────────────────────────────────────── */

import React from "react";
import { formatCurrency } from "../../../utils/currency";
import { fadeDenLabel } from "../../../config/valuation";
import { fmtNum } from "../../../config/earningsNote";
import { Banner, Pill } from "./earningsNoteUI";

const Row = ({ label, children }) => (
  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", lineHeight: 1.7 }}>
    <span style={{ opacity: 0.75, minWidth: 132 }}>{label}</span>
    <span style={{ fontWeight: "var(--font-weight-semibold)" }}>{children}</span>
  </div>
);

const ConsensusAnchorNote = ({ anchor }) => {
  if (!anchor) return null;

  if (anchor.on === false) {
    return (
      <Banner kind="plain" style={{ marginTop: "var(--spacing-md)" }}>
        <strong>No consensus anchor.</strong> The projection runs the model's own organic path —
        analyst FY1/FY2 estimates are not pinned into years 1–2 for this name.
      </Banner>
    );
  }

  const fade = fadeDenLabel(anchor.fade_den);

  return (
    <Banner kind="success" style={{ marginTop: "var(--spacing-md)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 6,
        }}
      >
        <strong>Consensus anchor on</strong>
        {fade && <Pill tone="pos">{fade.title}</Pill>}
        {anchor.mom_high && <Pill tone="pos">Momentum extended</Pill>}
        {anchor.cyclical_peak && <Pill tone="warn">Cyclical peak</Pill>}
        {anchor.rev_anchor_on && <Pill>Revenue anchored</Pill>}
      </div>

      <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 400 }}>
        Years 1–2 net income is pinned to analyst consensus EPS × diluted shares, then fades back
        to the model's own path.
        {fade && ` ${fade.detail}`}
      </div>

      <div
        style={{
          marginTop: 8,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "2px 18px",
          fontSize: "var(--font-size-xs)",
          fontWeight: 400,
        }}
      >
        {anchor.eps_fy1 != null && (
          <Row label="Consensus EPS FY1">{formatCurrency(anchor.eps_fy1)}</Row>
        )}
        {anchor.eps_fy2 != null && (
          <Row label="Consensus EPS FY2">{formatCurrency(anchor.eps_fy2)}</Row>
        )}
        {anchor.revenue_fy1 != null && (
          <Row label="Consensus revenue FY1">
            {formatCurrency(anchor.revenue_fy1, { compact: true })}
          </Row>
        )}
        {anchor.anchored_years != null && (
          <Row label="Anchored years">{fmtNum(anchor.anchored_years, 0)}</Row>
        )}
        {anchor.anchor_ratio_y1 != null && (
          <Row label="Anchor ratio Y1">{fmtNum(anchor.anchor_ratio_y1, 4)}×</Row>
        )}
        {anchor.anchor_ratio_y2 != null && (
          <Row label="Anchor ratio Y2">{fmtNum(anchor.anchor_ratio_y2, 4)}×</Row>
        )}
        {anchor.rev_anchor_ratio_y1 != null && (
          <Row label="Revenue ratio Y1">{fmtNum(anchor.rev_anchor_ratio_y1, 4)}×</Row>
        )}
        {anchor.mom_ratio != null && (
          <Row label="Price ÷ 200-DMA">{fmtNum(anchor.mom_ratio, 4)}×</Row>
        )}
        {anchor.fade_den != null && <Row label="Fade denominator">{anchor.fade_den}</Row>}
        {anchor.source && <Row label="Source">{anchor.source}</Row>}
      </div>

      {Array.isArray(anchor.margin_capped_years) && anchor.margin_capped_years.length > 0 && (
        <div style={{ marginTop: 8, fontSize: "var(--font-size-xs)", fontWeight: 400 }}>
          Margin was capped in: {anchor.margin_capped_years.join(", ")}.
        </div>
      )}
    </Banner>
  );
};

export default ConsensusAnchorNote;
